import React, { FC, useCallback, useEffect, useState } from 'react'
import Item from '../item/item'
import * as routes from '../../../../constants/routes'
import { ArrowDown, BarChart, Briefcase, Letter, Globe, House, Newspaper, Tags } from '../../../common/icons'
import { Picture, QuestionCircleFilled, TestingIcon, People, Star, Results } from '../../../common/icons'
import { addCountlyEvent } from '../../../../utils/stats'
import Group from '../../../common/icons/group'
import Sections from '../../../common/icons/sections'
import Questions from '../../../common/icons/questions'
import { Menu as AntMenu, Tooltip } from 'antd'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store/store'
import './ico-menu.ant.scss'
import useCheckUserProfile from '../../../../hooks/useCheckUserProfile'
import useInvisibleScroll from '../../../survey/hooks/invisible-scoll'
import { ContainerOutlined } from '@ant-design/icons'

const TESTING_MENU_COLLAPSED_KEY = 'testingMenuCollapsed'
const TESTING_MENU_COLLAPSED_EVENT = 'testingMenuCollapsedChange'

const getInitialTestingMenuCollapsed = () => {
  if (typeof window === 'undefined') {
    return false
  }

  return window.localStorage.getItem(TESTING_MENU_COLLAPSED_KEY) === 'true'
}

const IcoMenu: FC = () => {
  const location = useLocation()
  const rootLocation = /^\/[^/]+/g.exec(location.pathname)
  const user = useSelector((state: RootState) => state.account.user)
  const userAdminOrExpert = user?.isAdmin || user?.competence?.isDivisionExpert
  const userCanUseMailing = userAdminOrExpert || user?.competence?.isDeputyExpert
  const [testingMenuCollapsed, setTestingMenuCollapsed] = useState(getInitialTestingMenuCollapsed)
  const path =
    (rootLocation &&
      (routes.SURVEYS_ADMIN.includes(rootLocation[0])
        ? '/' + location.pathname.split('/')[1] + '/' + location.pathname.split('/')[2]
        : rootLocation[0])) ||
    '/'
  useInvisibleScroll()
  useCheckUserProfile()

  useEffect(() => {
    const handleTestingMenuCollapsedChange = (event: Event) => {
      setTestingMenuCollapsed((event as CustomEvent<boolean>).detail)
    }

    window.addEventListener(TESTING_MENU_COLLAPSED_EVENT, handleTestingMenuCollapsedChange)

    return () => {
      window.removeEventListener(TESTING_MENU_COLLAPSED_EVENT, handleTestingMenuCollapsedChange)
    }
  }, [])

  const toggleTestingMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    setTestingMenuCollapsed((prevState) => {
      const nextState = !prevState

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(TESTING_MENU_COLLAPSED_KEY, String(nextState))
        window.dispatchEvent(new CustomEvent(TESTING_MENU_COLLAPSED_EVENT, { detail: nextState }))
      }

      return nextState
    })
  }, [])

  return (
    <AntMenu
      mode="inline"
      defaultSelectedKeys={['/']}
      selectedKeys={[path, (rootLocation && rootLocation[0]) || '/']}
      className={'customIcoMenu dy-scrollbar-off'}
    >
      <Item route={routes.MAIN} title={'Главная'} tooltip={'Главная'} icon={<House />} key={1} />
      <Item
        route={routes.ABOUT}
        title={'WorldSkills'}
        tooltip={'WorldSkills'}
        icon={<Globe />}
        key={2}
        onClick={() => addCountlyEvent('Переход в раздел О WorldSkills', { Источник: 'Меню' })}
      />
      <Item
        route={routes.NEWS}
        icon={<Newspaper />}
        title={'Новости'}
        tooltip={'Новости'}
        key={3}
        onClick={() => addCountlyEvent('Переход в раздел новостей', { Источник: 'Меню' })}
      />
      <Item route={routes.CHAMPIONSHIPS} title={'Чемпионаты'} tooltip={'Чемпионаты'} icon={<BarChart />} key={4} />
      {/* Добавить условие в зависимости от того, есть ли доступ к разделу */}
      <Item route={routes.RATING} title={'Оценки'} tooltip={'Оценки'} icon={<Star />} key={18} />
      {userAdminOrExpert ? (
        <Item route={routes.PARTICIPANTS} title={'Участники'} tooltip={'Участники'} icon={<People />} key={5} />
      ) : null}
      <Item route={routes.COMPETENCIES} title={'Компетенции'} tooltip={'Компетенции'} icon={<Briefcase />} key={6} />
      <Item route={routes.EVENTS} title={'События'} tooltip={'События'} icon={<Tags />} key={7} />
      <Item route={routes.GALLERY} title={'Галерея'} tooltip={'Галерея'} icon={<Picture />} key={8} />
      {/*TODO: условия по админу / обычному пользователю*/}
      {userAdminOrExpert ? (
        <>
          <AntMenu.Item key={99} className={'item-line'} />

          <Item
            route={routes.SURVEYS_CONTROL}
            title={'Тестирование'}
            tooltip={'Тестирование'}
            icon={<TestingIcon />}
            className={'up-line testing-menu-control'}
            showTitle={false}
            extra={
              <Tooltip
                title={testingMenuCollapsed ? 'Развернуть блок тестирований' : 'Свернуть блок тестирований'}
                placement={'right'}
                mouseEnterDelay={0.2}
              >
                <button
                  type={'button'}
                  className={`testing-menu-control__toggle${testingMenuCollapsed ? ' collapsed' : ''}`}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  onClick={toggleTestingMenu}
                  aria-label={testingMenuCollapsed ? 'Развернуть блок тестирований' : 'Свернуть блок тестирований'}
                >
                  <ArrowDown color="#025EA1" width={12} height={6} />
                </button>
              </Tooltip>
            }
            key={12}
          />
          <Item
            route={routes.SURVEYS_GROUP}
            title={'Группы'}
            tooltip={'Группы'}
            icon={<Group />}
            className={`testing-menu-child${testingMenuCollapsed ? ' collapsed' : ''}`}
            key={13}
          />
          <Item
            route={routes.SURVEYS_SECTION}
            title={'Секции'}
            tooltip={'Секции'}
            icon={<Sections />}
            className={`testing-menu-child${testingMenuCollapsed ? ' collapsed' : ''}`}
            key={14}
          />
          <Item
            route={routes.SURVEYS_QUESTIONS}
            title={'Вопросы'}
            tooltip={'Вопросы'}
            icon={<Questions />}
            className={`testing-menu-child${testingMenuCollapsed ? ' collapsed' : ''}`}
            key={15}
          />
          <Item
            route={routes.SURVEYS_RESULTS}
            title={'Результаты'}
            tooltip={'Результаты'}
            icon={<Results />}
            className={`testing-menu-child${testingMenuCollapsed ? ' collapsed' : ''}`}
            key={16}
          />
          <AntMenu.Item key={98} className={'item-line'} />
        </>
      ) : (
        <>
          {!user || (
            <Item
              route={routes.SURVEYS}
              title={'Тестирование'}
              tooltip={'Тестирование'}
              icon={<TestingIcon />}
              key={11}
            />
          )}
        </>
      )}
      {!userAdminOrExpert && userCanUseMailing ? <AntMenu.Item key={97} className={'item-line'} /> : null}
      {userCanUseMailing ? (
        <Item route={routes.MAILING} title={'Рассылка'} tooltip={'Рассылка'} icon={<Letter />} key={19} />
      ) : null}
      <Item
        route={routes.VOTING}
        title={'Голосования'}
        tooltip={'Голосования'}
        icon={<ContainerOutlined style={{ fontSize: '24px', color: '#818C99' }} />}
        key={17}
      />
      <Item route={routes.FAQ} title={'FAQ'} tooltip={'FAQ'} icon={<QuestionCircleFilled />} key={9} />
      <Item
        route={routes.CONTACTS}
        title={'Написать нам'}
        tooltip={'Написать нам'}
        icon={<Letter />}
        key={10}
        onClick={() => addCountlyEvent('Обратная связь', { Действие: 'Форма открыта', Источник: 'Меню' })}
      />
    </AntMenu>
  )
}

export default IcoMenu
